// Stuck-submission modal — surfaced by useOfflineSubmit on the next form
// open after one or more submissions have failed 3 times. Each row exposes:
//   - last error (truncated)
//   - first-attempt date
//   - Retry  (resets retry_count and fires sync immediately)
//   - Discard (removes the row outright)
//
// Phase 1B canary uses this for fuel_supply only; Phase 1C reuses across
// the 4 fan-out forms. Keep the rendering payload-shape-agnostic — the
// modal shows a one-line label per row plus the timestamp; form-specific
// detail comes from the queue entry's payload field if a future caller
// wants to render more.

import React from 'react';
import type {QueuedSubmission} from '../lib/offlineQueue.js';

// The rows this modal renders ARE offlineQueue rows — useOfflineSubmit hands
// its `stuckRows` straight through — so offlineQueue owns the shape and this
// name is an alias, not a second declaration of it.
//
// It used to be a hand-written subset carrying only the six members the modal
// itself reads. That was wrong in one specific way: `describeRow` is the modal's
// escape hatch for form-specific detail, every caller reaches `row.payload`
// through it, and a subset that omitted `payload` made the callback
// unimplementable (parameters are contravariant, so a caller could not widen it
// back). Deferring to the queue's own type fixes that without the modal
// claiming to know what any payload contains — `payload` is `unknown` there,
// and each form narrows it.
export type StuckSubmissionRow = QueuedSubmission;

// Read one member off a queued payload/record. offlineQueue types both columns
// `unknown` because their shape is per-form jsonb, so a describeRow has to
// prove a member is there before it reads it. Returns undefined for a
// non-object, which is exactly what the `args && args.parent_in` chains the
// callers already wrote were testing for.
export function payloadMember(value: unknown, key: string): unknown {
  if (typeof value !== 'object' || value === null) return undefined;
  if (!(key in value)) return undefined;
  return Reflect.get(value, key);
}

// The same read, rendered. Non-string values are stringified; a missing, null
// or blank one yields the caller's fallback, which preserves each form's
// `payload.x || '?'` idiom exactly.
export function payloadText(value: unknown, key: string, fallback = '?'): string {
  const member = payloadMember(value, key);
  if (member === null || member === undefined) return fallback;
  const text = String(member);
  return text === '' ? fallback : text;
}

function fmtTs(ms: number | null | undefined) {
  if (!ms) return '—';
  const d = new Date(ms);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(d.getMonth() + 1)}/${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function truncate(s: string | null | undefined, max: number) {
  if (!s) return '';
  return s.length > max ? s.slice(0, max - 1) + '…' : s;
}

// `onRetry`/`onDiscard` are async at every call site but their promises are not
// awaited here, so the callbacks are typed by what the modal actually needs.
type StuckSubmissionsModalProps = {
  rows?: readonly StuckSubmissionRow[] | null;
  onRetry: (csid: string) => void;
  onDiscard: (csid: string) => void;
  onClose: () => void;
  onOpen?: (csid: string) => void;
  busy?: boolean;
  canRetry?: (row: StuckSubmissionRow) => boolean;
  error?: string;
  formLabel?: string;
  describeRow?: (row: StuckSubmissionRow) => React.ReactNode;
};

export default function StuckSubmissionsModal({
  rows,
  onRetry,
  onDiscard,
  onClose,
  onOpen,
  busy = false,
  canRetry = () => true,
  error,
  formLabel = 'submission',
  describeRow,
}: StuckSubmissionsModalProps) {
  if (!rows || rows.length === 0) return null;

  return (
    <div
      role="dialog"
      aria-label="Stuck submissions"
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0,0,0,.55)',
        zIndex: 9100,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '1rem',
      }}
    >
      <div
        style={{
          background: 'white',
          borderRadius: 12,
          padding: '20px 22px',
          maxWidth: 520,
          width: '100%',
          boxShadow: '0 8px 32px rgba(0,0,0,.25)',
          maxHeight: '80vh',
          overflowY: 'auto',
        }}
      >
        <div style={{fontSize: 20, marginBottom: 6}}>⚠️</div>
        <div style={{fontSize: 15, fontWeight: 700, color: 'var(--ink)', marginBottom: 4}}>
          Needs Help: {rows.length} {formLabel}
          {rows.length === 1 ? '' : 's'}
        </div>
        <div style={{fontSize: 12, color: 'var(--ink-muted)', marginBottom: 14, lineHeight: 1.5}}>
          These submissions are still on this device.{' '}
          {onOpen
            ? 'Open a report to review its answers and photos, or retry a temporary failure.'
            : 'Retry a temporary failure.'}{' '}
          Discard only after you have resolved the report.
        </div>
        {error && (
          <div role="alert" style={{color: 'var(--danger-ink)', marginBottom: 12}}>
            {error}
          </div>
        )}

        <div style={{display: 'flex', flexDirection: 'column', gap: 10}}>
          {rows.map((row) => {
            const summary = describeRow ? describeRow(row) : `${row.form_kind} · ${row.csid.slice(0, 12)}`;
            return (
              <div
                key={row.csid}
                data-stuck-csid={row.csid}
                style={{
                  border: '1px solid #fde68a',
                  background: '#fffbeb',
                  borderRadius: 10,
                  padding: '10px 12px',
                  fontSize: 12,
                  color: '#78716c',
                }}
              >
                <div style={{fontWeight: 600, color: 'var(--ink)', marginBottom: 2}}>{summary}</div>
                {row.form_kind === 'webform_hub_draft' ? (
                  (() => {
                    const rec = (row.record && typeof row.record === 'object' ? row.record : null) as {
                      hub_carrier_ram?: unknown;
                    } | null;
                    const ramState = rec ? rec.hub_carrier_ram : null;
                    if (ramState === 'writing') {
                      return (
                        <div style={{marginBottom: 8, fontSize: 11, color: 'var(--ink-faint)'}}>
                          Keeping on this device… (review only)
                        </div>
                      );
                    }
                    if (ramState) {
                      return (
                        <>
                          {row.last_error && (
                            <div style={{marginBottom: 4, fontSize: 11, color: '#b91c1c'}}>
                              {truncate(row.last_error, 80)}
                            </div>
                          )}
                          <div style={{marginBottom: 8, fontSize: 11, color: '#b91c1c'}}>
                            NOT saved on this device · Kept only while tab stays open (review only)
                          </div>
                        </>
                      );
                    }
                    return (
                      <div style={{marginBottom: 8, fontSize: 11, color: 'var(--ink-faint)'}}>
                        Saved {fmtTs(row.created_at)} · On device (review only)
                      </div>
                    );
                  })()
                ) : (
                  <>
                    <div style={{marginBottom: 4}}>
                      Last error: <span style={{color: '#b91c1c'}}>{truncate(row.last_error || 'unknown', 80)}</span>
                    </div>
                    <div style={{marginBottom: 8, fontSize: 11, color: 'var(--ink-faint)'}}>
                      Queued {fmtTs(row.created_at)}
                      {row.last_attempt_at
                        ? ` · Last try ${fmtTs(row.last_attempt_at)} · ${row.retry_count} attempts`
                        : ' · Not sent'}
                    </div>
                  </>
                )}
                <div style={{display: 'flex', gap: 8}}>
                  {onOpen && (
                    <button type="button" data-stuck-action="open" disabled={busy} onClick={() => onOpen(row.csid)}>
                      Open
                    </button>
                  )}
                  {canRetry(row) && (
                    <button
                      disabled={busy}
                      onClick={() => onRetry(row.csid)}
                      data-stuck-action="retry"
                      style={{
                        padding: '6px 14px',
                        borderRadius: 10,
                        border: 'none',
                        background: '#187A54',
                        color: 'white',
                        fontSize: 12,
                        fontWeight: 600,
                        cursor: 'pointer',
                        fontFamily: 'inherit',
                      }}
                    >
                      Retry
                    </button>
                  )}
                  <button
                    disabled={busy}
                    onClick={() => onDiscard(row.csid)}
                    data-stuck-action="discard"
                    style={{
                      padding: '6px 14px',
                      borderRadius: 10,
                      border: '1px solid var(--border-strong)',
                      background: 'white',
                      color: '#b91c1c',
                      fontSize: 12,
                      fontWeight: 600,
                      cursor: 'pointer',
                      fontFamily: 'inherit',
                    }}
                  >
                    Discard
                  </button>
                </div>
              </div>
            );
          })}
        </div>

        <div style={{display: 'flex', justifyContent: 'flex-end', marginTop: 16}}>
          <button
            disabled={busy}
            onClick={onClose}
            style={{
              padding: '8px 18px',
              borderRadius: 10,
              border: '1px solid var(--border-strong)',
              background: 'white',
              color: 'var(--ink)',
              fontSize: 13,
              fontWeight: 500,
              cursor: 'pointer',
              fontFamily: 'inherit',
            }}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
